import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, CardBody, Chip, Input, Skeleton } from "@heroui/react";
import { Link } from "@tanstack/react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBoxArchive,
    faBoxOpen,
    faDrawPolygon,
    faPencil,
    faSave,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import * as turf from "@turf/turf";
import {
    Feature,
    FeatureCollection,
    LineString,
    MultiPolygon,
    Polygon,
} from "geojson";
import { GeoJSONSource, IControl, Map as MapboxMap } from "mapbox-gl";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import useConfirm from "@/lib/confirm/useConfirm";
import useMap from "@/lib/map/useMap";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    polygonsOverlap,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "@/lib/utils/map";
import { useI18n } from "@/lib/i18n/useI18n";
import { COLOR_MAP } from "@/types/color";
import { CropZone, DEFAULT_CROP_ZONE_COLOR } from "@/types/cropZone";
import { Status } from "@/types/enums";
import { useCropZoneHighlight } from "@/components/field/setup/CropZoneHighlight";
import CropZoneColorPicker from "@/components/field/setup/CropZoneColorPicker";

function zoneFeature(
    zone: CropZone,
): Feature<MultiPolygon> | null {
    if (!zone.boundary) return null;
    return {
        type: "Feature",
        properties: {},
        geometry: zone.boundary,
    };
}

function overlappingActiveZones(
    zone: CropZone,
    activeZones: CropZone[],
): CropZone[] {
    const candidate = zoneFeature(zone);
    if (!candidate) return [];
    return activeZones.filter((active) => {
        if (active.id === zone.id) return false;
        const activeFeature = zoneFeature(active);
        if (!activeFeature) return false;
        return polygonsOverlap(candidate, activeFeature);
    });
}

type DraftCropZone = {
    drawId: string;
    name: string;
    color: string;
    boundary: MultiPolygon;
};

const ZONE_COLOR_EXPR = [
    "case",
    ["has", "user_color"],
    ["get", "user_color"],
    "#3bb2d0",
];

const DRAW_STYLES = MapboxDraw.lib.theme.map((style) => {
    const id = style.id as string;
    if (id === "gl-draw-polygon-fill") {
        return {
            ...style,
            paint: {
                ...style.paint,
                "fill-color": ZONE_COLOR_EXPR,
                "fill-opacity": 0.45,
            },
        };
    }
    if (id === "gl-draw-lines") {
        return {
            ...style,
            paint: {
                ...style.paint,
                "line-color": [
                    "case",
                    ["all", ["==", "$type", "Polygon"], ["has", "user_color"]],
                    ["get", "user_color"],
                    ["==", ["get", "active"], "true"],
                    "#fbb03b",
                    "#3bb2d0",
                ],
            },
        };
    }
    return style;
});

const SNAP_SOURCE = "crop-zone-snap";
const SNAP_LAYER = "crop-zone-snap-layer";
const SNAP_PIXELS = 20;

const EMPTY_SNAP: FeatureCollection = {
    type: "FeatureCollection",
    features: [],
};

type SnapContext = {
    map: MapboxMap;
    _ctx: { api: MapboxDraw };
};

function polygonCoordinates(feature: Feature): number[][] {
    if (feature.geometry?.type === "Polygon") {
        return feature.geometry.coordinates.flat();
    }
    if (feature.geometry?.type === "MultiPolygon") {
        return feature.geometry.coordinates.flat(2);
    }
    return [];
}

function showSnap(map: MapboxMap, coordinate: [number, number] | null) {
    const source = map.getSource(SNAP_SOURCE);
    if (!source || source.type !== "geojson") return;
    (source as GeoJSONSource).setData(
        coordinate
            ? {
                  type: "Feature",
                  properties: {},
                  geometry: { type: "Point", coordinates: coordinate },
              }
            : EMPTY_SNAP,
    );
    if (coordinate && map.getLayer(SNAP_LAYER)) {
        map.moveLayer(SNAP_LAYER);
    }
}

function nearestVertex(
    map: MapboxMap,
    features: Feature[],
    lngLat: { lng: number; lat: number },
    excludeId?: string | number,
): [number, number] | null {
    const cursor = map.project([lngLat.lng, lngLat.lat]);
    let closest: [number, number] | null = null;
    let closestDistance = SNAP_PIXELS;
    features.forEach((feature) => {
        if (excludeId != null && String(feature.id) === String(excludeId)) {
            return;
        }
        polygonCoordinates(feature).forEach((coordinate) => {
            if (coordinate.length < 2) return;
            const projected = map.project([coordinate[0], coordinate[1]]);
            const distance = Math.hypot(
                projected.x - cursor.x,
                projected.y - cursor.y,
            );
            if (distance > closestDistance) return;
            closestDistance = distance;
            closest = [coordinate[0], coordinate[1]];
        });
    });
    return closest;
}

function asDrawMode(ctx: SnapContext) {
    return ctx as unknown as MapboxDraw.DrawCustomModeThis &
        MapboxDraw.DrawCustomMode;
}

function applyVertexSnap(
    ctx: SnapContext,
    event: { lngLat: { lng: number; lat: number } },
    excludeId?: string | number,
) {
    const snapped = nearestVertex(
        ctx.map,
        ctx._ctx.api.getAll().features,
        event.lngLat,
        excludeId,
    );
    if (!snapped) {
        showSnap(ctx.map, null);
        return;
    }
    event.lngLat.lng = snapped[0];
    event.lngLat.lat = snapped[1];
    showSnap(ctx.map, snapped);
}

const drawLineStringMode = MapboxDraw.modes.draw_line_string;

const snapLineMode = {
    ...drawLineStringMode,
    onMouseMove(
        state: { line?: { id?: string | number } },
        event: MapboxDraw.MapMouseEvent,
    ) {
        const ctx = this as unknown as SnapContext;
        applyVertexSnap(ctx, event, state.line?.id);
        drawLineStringMode.onMouseMove?.call(asDrawMode(ctx), state, event);
    },
    onClick(
        state: { line?: { id?: string | number } },
        event: MapboxDraw.MapMouseEvent,
    ) {
        const ctx = this as unknown as SnapContext;
        applyVertexSnap(ctx, event, state.line?.id);
        drawLineStringMode.onClick?.call(asDrawMode(ctx), state, event);
    },
    onTap(
        state: { line?: { id?: string | number } },
        event: MapboxDraw.MapTouchEvent,
    ) {
        const ctx = this as unknown as SnapContext;
        applyVertexSnap(ctx, event, state.line?.id);
        drawLineStringMode.onTap?.call(asDrawMode(ctx), state, event);
    },
    onTouchMove(
        state: { line?: { id?: string | number } },
        event: MapboxDraw.MapTouchEvent,
    ) {
        const ctx = this as unknown as SnapContext;
        applyVertexSnap(ctx, event, state.line?.id);
        drawLineStringMode.onMouseMove?.call(
            asDrawMode(ctx),
            state,
            event as unknown as MapboxDraw.MapMouseEvent,
        );
    },
    onStop(state: { line?: { id?: string | number } }) {
        const ctx = this as unknown as SnapContext;
        showSnap(ctx.map, null);
        drawLineStringMode.onStop?.call(asDrawMode(ctx), state);
    },
    onTrash(state: { line?: { id?: string | number } }) {
        const ctx = this as unknown as SnapContext;
        showSnap(ctx.map, null);
        drawLineStringMode.onTrash?.call(asDrawMode(ctx), state);
    },
};

type DrawBarButton = {
    on: string;
    action: (event: Event) => void;
    classes?: string[];
    title?: string;
    elButton?: HTMLButtonElement;
};

class ExtendDrawBar implements IControl {
    private buttons: DrawBarButton[];
    private onAddOrig: MapboxDraw["onAdd"];
    private onRemoveOrig: MapboxDraw["onRemove"];
    private elContainer: HTMLElement | null = null;

    constructor(options: { draw: MapboxDraw; buttons?: DrawBarButton[] }) {
        this.buttons = options.buttons ?? [];
        this.onAddOrig = options.draw.onAdd.bind(options.draw);
        this.onRemoveOrig = options.draw.onRemove.bind(options.draw);
    }

    onAdd(map: MapboxMap) {
        this.elContainer = this.onAddOrig(map);
        this.buttons.forEach((button) => this.addButton(button));
        return this.elContainer;
    }

    onRemove(map: MapboxMap) {
        this.buttons.forEach((button) => this.removeButton(button));
        this.onRemoveOrig(map);
    }

    private addButton(options: DrawBarButton) {
        if (!this.elContainer) return;
        const elButton = document.createElement("button");
        elButton.type = "button";
        elButton.className = "mapbox-gl-draw_ctrl-draw-btn";
        if (options.title) {
            elButton.title = options.title;
            elButton.setAttribute("aria-label", options.title);
        }
        options.classes?.forEach((className) => {
            elButton.classList.add(className);
        });
        elButton.addEventListener(options.on, options.action);
        this.elContainer.appendChild(elButton);
        options.elButton = elButton;
    }

    private removeButton(options: DrawBarButton) {
        if (!options.elButton) return;
        options.elButton.removeEventListener(options.on, options.action);
        options.elButton.remove();
        options.elButton = undefined;
    }
}

const CHECK_ICON =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#16a34a" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
const CANCEL_ICON =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#dc2626" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

class SplitActionsControl implements IControl {
    private elContainer: HTMLElement | null = null;
    private onDone: () => void;
    private onCancel: () => void;
    private doneTitle: string;
    private cancelTitle: string;

    constructor(options: {
        onDone: () => void;
        onCancel: () => void;
        doneTitle: string;
        cancelTitle: string;
    }) {
        this.onDone = options.onDone;
        this.onCancel = options.onCancel;
        this.doneTitle = options.doneTitle;
        this.cancelTitle = options.cancelTitle;
    }

    onAdd() {
        const container = document.createElement("div");
        container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
        container.style.display = "none";
        container.appendChild(
            this.createButton(CHECK_ICON, this.doneTitle, this.onDone),
        );
        container.appendChild(
            this.createButton(CANCEL_ICON, this.cancelTitle, this.onCancel),
        );
        this.elContainer = container;
        return container;
    }

    onRemove() {
        this.elContainer?.remove();
        this.elContainer = null;
    }

    setVisible(visible: boolean) {
        if (!this.elContainer) return;
        this.elContainer.style.display = visible ? "" : "none";
    }

    private createButton(icon: string, title: string, action: () => void) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "split-action-btn";
        button.title = title;
        button.setAttribute("aria-label", title);
        button.innerHTML = icon;
        button.addEventListener("click", action);
        return button;
    }
}

function toMultiPolygon(geometry: Polygon | MultiPolygon): MultiPolygon {
    if (geometry.type === "MultiPolygon") return geometry;
    return { type: "MultiPolygon", coordinates: [geometry.coordinates] };
}

function fieldBoundaryFeatures(
    fieldBoundary: Feature<MultiPolygon>,
): FeatureCollection {
    const geometry = fieldBoundary.geometry;
    if (geometry.type === "MultiPolygon") {
        return {
            type: "FeatureCollection",
            features: geometry.coordinates.map((coordinates, index) => ({
                type: "Feature" as const,
                id: `field-boundary-${index}`,
                properties: {},
                geometry: {
                    type: "Polygon" as const,
                    coordinates,
                },
            })),
        };
    }
    return {
        type: "FeatureCollection",
        features: [
            {
                type: "Feature",
                id: "field-boundary-0",
                properties: {},
                geometry,
            },
        ],
    };
}

function explodeDrawnPolygons(draw: MapboxDraw) {
    const features = draw.getAll().features;
    const nextFeatures: Feature<Polygon>[] = [];
    let changed = false;

    features.forEach((feature) => {
        if (feature.geometry?.type === "Polygon") {
            nextFeatures.push(feature as Feature<Polygon>);
            return;
        }
        if (feature.geometry?.type !== "MultiPolygon") return;
        changed = true;
        feature.geometry.coordinates.forEach((coordinates) => {
            nextFeatures.push({
                type: "Feature",
                properties: { ...(feature.properties ?? {}) },
                geometry: {
                    type: "Polygon",
                    coordinates,
                },
            });
        });
    });

    if (!changed) return;
    draw.set({
        type: "FeatureCollection",
        features: nextFeatures,
    });
}

function flattenToPolygons(
    feature: Feature<Polygon | MultiPolygon>,
): Feature<Polygon>[] {
    if (feature.geometry.type === "Polygon") {
        return [feature as Feature<Polygon>];
    }
    return feature.geometry.coordinates.map((coordinates) =>
        turf.polygon(coordinates, { ...(feature.properties ?? {}) }),
    );
}

function splitPolygonWithLine(
    polygon: Feature<Polygon>,
    line: Feature<LineString>,
): Feature<Polygon>[] {
    if (turf.booleanDisjoint(polygon, line)) return [polygon];
    if (turf.lineIntersect(polygon, line).features.length < 2) return [polygon];

    const coords = line.geometry.coordinates;
    if (coords.length < 2) return [polygon];

    const [minX, minY, maxX, maxY] = turf.bbox(polygon);
    const span = Math.max(maxX - minX, maxY - minY, 0.001);

    const extend = (from: number[], toward: number[]): [number, number] => {
        const dx = from[0] - toward[0];
        const dy = from[1] - toward[1];
        const len = Math.hypot(dx, dy) || 1;
        return [
            from[0] + (dx / len) * span * 10,
            from[1] + (dy / len) * span * 10,
        ];
    };

    const first = coords[0];
    const second = coords[1];
    const last = coords[coords.length - 1];
    const secondLast = coords[coords.length - 2];
    const extendedCoords: [number, number][] = [
        extend(first, second),
        ...coords.slice(1, -1).map((c) => [c[0], c[1]] as [number, number]),
        extend(last, secondLast),
    ];

    const start = extendedCoords[0];
    const end = extendedCoords[extendedCoords.length - 1];
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const len = Math.hypot(dx, dy) || 1;
    const px = (-dy / len) * span * 20;
    const py = (dx / len) * span * 20;

    const sideA = turf.polygon([
        [
            ...extendedCoords,
            ...[...extendedCoords]
                .reverse()
                .map(([x, y]) => [x + px, y + py] as [number, number]),
            extendedCoords[0],
        ],
    ]);
    const sideB = turf.polygon([
        [
            ...extendedCoords,
            ...[...extendedCoords]
                .reverse()
                .map(([x, y]) => [x - px, y - py] as [number, number]),
            extendedCoords[0],
        ],
    ]);

    const parts: Feature<Polygon>[] = [];
    for (const side of [sideA, sideB]) {
        try {
            const clipped = turf.intersect(
                turf.featureCollection([polygon, side]),
            );
            if (!clipped) continue;
            if (clipped.geometry.type === "Polygon") {
                parts.push(
                    turf.polygon(clipped.geometry.coordinates, {
                        ...(polygon.properties ?? {}),
                    }),
                );
            } else if (clipped.geometry.type === "MultiPolygon") {
                for (const coordinates of clipped.geometry.coordinates) {
                    parts.push(
                        turf.polygon(coordinates, {
                            ...(polygon.properties ?? {}),
                        }),
                    );
                }
            }
        } catch {
            continue;
        }
    }

    return parts.length >= 2 ? parts : [polygon];
}

function CropZones() {
    const { t } = useTranslation();
    const { formatUnit } = useI18n();
    const { showConfirm } = useConfirm();
    const { useMutateRequest } = useRequest();
    const { map, mapLoaded } = useMap();
    const { fieldId, fieldQuery, geometryQuery } = useFieldDashboard() ?? {};
    const draw = useRef<MapboxDraw | null>(null);
    const drawBar = useRef<ExtendDrawBar | null>(null);
    const splitActions = useRef<SplitActionsControl | null>(null);

    const [isDrawing, setIsDrawing] = useState(false);
    const [drafts, setDrafts] = useState<DraftCropZone[]>([]);
    const { setHoveredZoneId, setHighlightsSuppressed } =
        useCropZoneHighlight();

    const cropZones = useMemo(() => {
        return fieldQuery?.data?.crop_zones ?? [];
    }, [fieldQuery?.data?.crop_zones]);

    const activeCropZones = useMemo(() => {
        return cropZones.filter((zone) => zone.status !== Status.Inactive);
    }, [cropZones]);

    const fieldBoundary = useMemo(() => {
        return geometryQuery?.data?.field ?? null;
    }, [geometryQuery?.data?.field]);

    const archiveCropZone = useMutateRequest({
        successMessage: t("success.crop_zone_archived_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_archive_crop_zone", {
            ns: "messages",
        }),
    });

    const unarchiveCropZone = useMutateRequest({
        successMessage: t("success.crop_zone_unarchived_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_unarchive_crop_zone", {
            ns: "messages",
        }),
    });

    const upsertCropZones = useMutateRequest({
        successMessage: t("success.crop_zones_saved_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_save_crop_zones", {
            ns: "messages",
        }),
    });

    const syncDraftsFromDraw = useCallback(() => {
        if (!draw.current) return;
        explodeDrawnPolygons(draw.current);
        const features = draw.current
            .getAll()
            .features.filter(
                (feature): feature is Feature<Polygon | MultiPolygon> =>
                    feature.geometry?.type === "Polygon" ||
                    feature.geometry?.type === "MultiPolygon",
            );

        setDrafts((prev) => {
            const prevById = new Map(
                prev.map((draft) => [draft.drawId, draft]),
            );
            const next = features.map((feature, index) => {
                const drawId = String(feature.id);
                const existing = prevById.get(drawId);
                return {
                    drawId,
                    name:
                        existing?.name ??
                        t("crop_zones.unnamed", {
                            ns: "setup",
                            number: index + 1,
                        }),
                    color:
                        existing?.color ??
                        COLOR_MAP[index % COLOR_MAP.length] ??
                        DEFAULT_CROP_ZONE_COLOR,
                    boundary: toMultiPolygon(feature.geometry),
                };
            });
            next.forEach((draft) => {
                const current = features.find(
                    (feature) => String(feature.id) === draft.drawId,
                )?.properties?.color;
                if (current === draft.color) return;
                draw.current?.setFeatureProperty(
                    draft.drawId,
                    "color",
                    draft.color,
                );
            });
            return next;
        });
    }, [t]);

    const applyLineSplit = useCallback(
        (event: { features: Feature[] }) => {
            if (!draw.current) return;

            const lineFeature = event.features.find(
                (feature) => feature.geometry?.type === "LineString",
            ) as Feature<LineString> | undefined;

            if (!lineFeature) {
                syncDraftsFromDraw();
                return;
            }

            const lineId = lineFeature.id;
            let targets = draw.current
                .getSelected()
                .features.filter(
                    (feature): feature is Feature<Polygon | MultiPolygon> =>
                        feature.geometry?.type === "Polygon" ||
                        feature.geometry?.type === "MultiPolygon",
                );

            if (!targets.length) {
                targets = draw.current
                    .getAll()
                    .features.filter(
                        (feature): feature is Feature<Polygon | MultiPolygon> =>
                            (feature.geometry?.type === "Polygon" ||
                                feature.geometry?.type === "MultiPolygon") &&
                            String(feature.id) !== String(lineId) &&
                            !turf.booleanDisjoint(feature, lineFeature),
                    );
            }

            const targetIds = new Set(
                targets.map((feature) => String(feature.id)),
            );
            const nextFeatures: Feature[] = [];

            for (const feature of draw.current.getAll().features) {
                if (String(feature.id) === String(lineId)) continue;
                if (
                    !targetIds.has(String(feature.id)) ||
                    (feature.geometry?.type !== "Polygon" &&
                        feature.geometry?.type !== "MultiPolygon")
                ) {
                    nextFeatures.push(feature);
                    continue;
                }

                for (const polygon of flattenToPolygons(
                    feature as Feature<Polygon | MultiPolygon>,
                )) {
                    nextFeatures.push(
                        ...splitPolygonWithLine(polygon, lineFeature),
                    );
                }
            }

            draw.current.set({
                type: "FeatureCollection",
                features: nextFeatures,
            });
            draw.current.changeMode("simple_select");
            syncDraftsFromDraw();
        },
        [syncDraftsFromDraw],
    );

    const activateSplitMode = useCallback(() => {
        if (!draw.current) return;
        draw.current.changeMode("draw_line_string");
        splitActions.current?.setVisible(true);
    }, []);

    const finishSplit = useCallback(() => {
        if (!draw.current || draw.current.getMode() !== "draw_line_string")
            return;
        splitActions.current?.setVisible(false);
        draw.current.changeMode("simple_select");
    }, []);

    const cancelSplit = useCallback(() => {
        if (!draw.current || draw.current.getMode() !== "draw_line_string")
            return;
        splitActions.current?.setVisible(false);
        draw.current.trash();
    }, []);

    const onModeChange = useCallback((event: { mode: string }) => {
        splitActions.current?.setVisible(event.mode === "draw_line_string");
    }, []);

    const teardownDraw = useCallback(() => {
        if (!map) return;
        map.off("draw.create", applyLineSplit);
        map.off("draw.update", syncDraftsFromDraw);
        map.off("draw.delete", syncDraftsFromDraw);
        map.off("draw.modechange", onModeChange);
        if (splitActions.current && map.hasControl(splitActions.current)) {
            map.removeControl(splitActions.current);
        }
        if (drawBar.current && map.hasControl(drawBar.current)) {
            map.removeControl(drawBar.current);
        }
        splitActions.current = null;
        drawBar.current = null;
        draw.current = null;
        removeLayersIfExists(map, [SNAP_LAYER]);
        removeSourcesIfExists(map, [SNAP_SOURCE]);
        map.getCanvas().style.cursor = "";
    }, [map, applyLineSplit, syncDraftsFromDraw, onModeChange]);

    useEffect(() => {
        if (!isDrawing || !map || !mapLoaded || !fieldBoundary) return;

        const drawControl = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
                trash: true,
            },
            userProperties: true,
            defaultMode: "simple_select",
            modes: {
                ...MapboxDraw.modes,
                draw_line_string: snapLineMode,
            },
            styles: DRAW_STYLES,
        });
        draw.current = drawControl;

        const bar = new ExtendDrawBar({
            draw: drawControl,
            buttons: [
                {
                    on: "click",
                    action: activateSplitMode,
                    classes: ["split-polygon"],
                    title: t("crop_zones.split", { ns: "setup" }),
                },
            ],
        });
        drawBar.current = bar;
        map.addControl(bar, "top-left");

        const actions = new SplitActionsControl({
            onDone: finishSplit,
            onCancel: cancelSplit,
            doneTitle: t("crop_zones.split_done", { ns: "setup" }),
            cancelTitle: t("crop_zones.split_cancel", { ns: "setup" }),
        });
        splitActions.current = actions;
        map.addControl(actions, "top-left");

        addUpdateGeojsonSource(map, SNAP_SOURCE, {
            type: "geojson",
            data: EMPTY_SNAP,
        });
        addUpdateGeojsonLayers(map, [
            {
                id: SNAP_LAYER,
                type: "circle",
                source: SNAP_SOURCE,
                paint: {
                    "circle-radius": 8,
                    "circle-color": "#ffffff",
                    "circle-stroke-width": 3,
                    "circle-stroke-color": "#111111",
                },
            },
        ]);

        drawControl.set(fieldBoundaryFeatures(fieldBoundary));
        syncDraftsFromDraw();

        map.on("draw.create", applyLineSplit);
        map.on("draw.update", syncDraftsFromDraw);
        map.on("draw.delete", syncDraftsFromDraw);
        map.on("draw.modechange", onModeChange);

        return () => {
            teardownDraw();
        };
    }, [
        activateSplitMode,
        applyLineSplit,
        cancelSplit,
        finishSplit,
        onModeChange,
        fieldBoundary,
        isDrawing,
        map,
        mapLoaded,
        syncDraftsFromDraw,
        t,
        teardownDraw,
    ]);

    useEffect(() => {
        setHighlightsSuppressed(isDrawing);
        if (isDrawing) setHoveredZoneId(null);
        return () => setHighlightsSuppressed(false);
    }, [isDrawing, setHighlightsSuppressed, setHoveredZoneId]);

    const startDrawing = useCallback(() => {
        if (!fieldBoundary) return;
        setDrafts([]);
        setIsDrawing(true);
    }, [fieldBoundary]);

    const cancelDrawing = useCallback(() => {
        setDrafts([]);
        setIsDrawing(false);
    }, []);

    const updateDraft = useCallback(
        (
            drawId: string,
            patch: Partial<Pick<DraftCropZone, "name" | "color">>,
        ) => {
            setDrafts((prev) =>
                prev.map((draft) =>
                    draft.drawId === drawId ? { ...draft, ...patch } : draft,
                ),
            );
            if (patch.color) {
                draw.current?.setFeatureProperty(drawId, "color", patch.color);
            }
        },
        [],
    );

    const onSaveDrafts = useCallback(() => {
        if (!fieldId || !drafts.length) return;
        if (drafts.some((draft) => !draft.name.trim())) return;

        upsertCropZones.mutate(
            {
                url: `fields/${fieldId}/crop_zones/`,
                method: "put",
                data: drafts.map((draft) => ({
                    name: draft.name.trim(),
                    color: draft.color,
                    boundary: draft.boundary,
                })),
            },
            {
                onSuccess: () => {
                    fieldQuery?.refetch();
                    geometryQuery?.refetch();
                    cancelDrawing();
                },
            },
        );
    }, [
        fieldId,
        drafts,
        upsertCropZones,
        fieldQuery,
        geometryQuery,
        cancelDrawing,
    ]);

    const onArchive = useCallback(
        (zone: CropZone) => {
            showConfirm({
                title: t("crop_zones.archive_crop_zone_title", {
                    ns: "setup",
                }),
                description: t("crop_zones.archive_crop_zone_description", {
                    ns: "setup",
                    name: zone.name,
                }),
                color: "danger",
                onConfirm: async () => {
                    await archiveCropZone.mutateAsync(
                        {
                            url: `crop_zones/${zone.id}/`,
                            method: "delete",
                        },
                        {
                            onSuccess: () => {
                                fieldQuery?.refetch();
                                geometryQuery?.refetch();
                            },
                        },
                    );
                },
            });
        },
        [showConfirm, t, archiveCropZone, fieldQuery, geometryQuery],
    );

    const runUnarchive = useCallback(
        async (zone: CropZone) => {
            await unarchiveCropZone.mutateAsync(
                {
                    url: `crop_zones/${zone.id}/activate/`,
                    method: "post",
                },
                {
                    onSuccess: () => {
                        fieldQuery?.refetch();
                        geometryQuery?.refetch();
                    },
                },
            );
        },
        [unarchiveCropZone, fieldQuery, geometryQuery],
    );

    const onUnarchive = useCallback(
        (zone: CropZone) => {
            const overlapping = overlappingActiveZones(zone, activeCropZones);
            if (!overlapping.length) {
                void runUnarchive(zone);
                return;
            }
            showConfirm({
                title: t("crop_zones.unarchive_overlap_title", {
                    ns: "setup",
                }),
                description: t("crop_zones.unarchive_overlap_description", {
                    ns: "setup",
                    name: zone.name,
                    zones: overlapping.map((item) => item.name).join(", "),
                }),
                onConfirm: async () => {
                    await runUnarchive(zone);
                },
            });
        },
        [activeCropZones, runUnarchive, showConfirm, t],
    );

    if (!fieldId || fieldQuery?.isLoading) {
        return (
            <div className="flex flex-col gap-1 w-full">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
            </div>
        );
    }

    if (isDrawing) {
        return (
            <div className="flex flex-col gap-2 w-full">
                <div className="flex flex-row gap-2 items-center justify-between">
                    <p className="text-sm text-gray-600">
                        {t("crop_zones.draw_new_zones_hint", { ns: "setup" })}
                    </p>
                    <div className="flex flex-row gap-1 shrink-0">
                        <Button
                            size="sm"
                            variant="light"
                            startContent={<FontAwesomeIcon icon={faXmark} />}
                            onPress={cancelDrawing}
                        >
                            {t("crop_zones.cancel", { ns: "setup" })}
                        </Button>
                        <Button
                            size="sm"
                            color="primary"
                            startContent={<FontAwesomeIcon icon={faSave} />}
                            isLoading={upsertCropZones.isPending}
                            isDisabled={
                                drafts.length < 2 ||
                                drafts.some((draft) => !draft.name.trim()) ||
                                upsertCropZones.isPending
                            }
                            onPress={onSaveDrafts}
                        >
                            {t("crop_zones.save", { ns: "setup" })}
                        </Button>
                    </div>
                </div>
                {drafts.map((draft) => (
                    <Card key={draft.drawId} className="w-full">
                        <CardBody className="px-2 py-1.5">
                            <div className="flex flex-row gap-2 items-center">
                                <CropZoneColorPicker
                                    size="sm"
                                    value={draft.color}
                                    onChange={(color) =>
                                        updateDraft(draft.drawId, { color })
                                    }
                                    ariaLabel={t("crop_zones.color", {
                                        ns: "setup",
                                    })}
                                />
                                <Input
                                    size="sm"
                                    value={draft.name}
                                    onValueChange={(value) =>
                                        updateDraft(draft.drawId, {
                                            name: value,
                                        })
                                    }
                                    placeholder={t("crop_zones.name", {
                                        ns: "setup",
                                    })}
                                    className="flex-1"
                                    classNames={{
                                        inputWrapper: "h-7 min-h-7",
                                    }}
                                />
                            </div>
                        </CardBody>
                    </Card>
                ))}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-1 w-full">
            <div className="flex flex-row gap-2 items-center justify-end">
                <Button
                    size="sm"
                    color="primary"
                    startContent={<FontAwesomeIcon icon={faDrawPolygon} />}
                    isDisabled={!fieldBoundary}
                    onPress={startDrawing}
                >
                    {t("crop_zones.draw_new_zones", { ns: "setup" })}
                </Button>
            </div>
            {cropZones.map((zone) => {
                const isArchived = zone.status === Status.Inactive;
                const canArchive = activeCropZones.length > 1;
                const canUnarchive = Boolean(zone.boundary);
                return (
                    <Card
                        key={zone.id}
                        className="w-full"
                        onMouseEnter={() => {
                            if (!isArchived) setHoveredZoneId(zone.id);
                        }}
                        onMouseLeave={() => setHoveredZoneId(null)}
                    >
                        <CardBody className="px-2 py-1.5">
                            <div className="flex flex-row gap-2 items-center justify-between">
                                <div
                                    className={`flex flex-row gap-2 items-center min-w-0${
                                        isArchived ? " opacity-60" : ""
                                    }`}
                                >
                                    <span
                                        className="h-5 w-5 shrink-0 rounded-md"
                                        style={{
                                            backgroundColor:
                                                zone.color ||
                                                DEFAULT_CROP_ZONE_COLOR,
                                        }}
                                        aria-hidden
                                    />
                                    <p className="text-sm font-medium truncate">
                                        {zone.name}
                                        {zone.acres != null && (
                                            <span className="ml-1.5 font-normal text-gray-500">
                                                {Number(zone.acres).toFixed(1)}{" "}
                                                {formatUnit("ac", "ha")}
                                            </span>
                                        )}
                                    </p>
                                    {isArchived && (
                                        <Chip size="sm" variant="flat">
                                            {t("crop_zones.archived", {
                                                ns: "setup",
                                            })}
                                        </Chip>
                                    )}
                                </div>
                                <div className="flex flex-row gap-0 shrink-0">
                                    {!isArchived && (
                                        <>
                                            <Button
                                                size="sm"
                                                variant="light"
                                                isIconOnly
                                                className="min-w-7 w-7 h-7"
                                                as={Link}
                                                to="/dashboard/fields/$fieldId/setup/crop-zones/$cropZoneId"
                                                params={
                                                    {
                                                        fieldId: String(fieldId),
                                                        cropZoneId: String(
                                                            zone.id,
                                                        ),
                                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                    } as any
                                                }
                                                aria-label={t(
                                                    "crop_zones.edit",
                                                    {
                                                        ns: "setup",
                                                    },
                                                )}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faPencil}
                                                    size="sm"
                                                />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="light"
                                                color="danger"
                                                isIconOnly
                                                className="min-w-7 w-7 h-7"
                                                aria-label={t(
                                                    "crop_zones.archive",
                                                    {
                                                        ns: "setup",
                                                    },
                                                )}
                                                isDisabled={!canArchive}
                                                isLoading={
                                                    archiveCropZone.isPending
                                                }
                                                onPress={() => onArchive(zone)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faBoxArchive}
                                                    size="sm"
                                                />
                                            </Button>
                                        </>
                                    )}
                                    {isArchived && (
                                        <Button
                                            size="sm"
                                            variant="flat"
                                            color="primary"
                                            isIconOnly
                                            className="min-w-7 w-7 h-7"
                                            aria-label={t(
                                                "crop_zones.unarchive",
                                                {
                                                    ns: "setup",
                                                },
                                            )}
                                            isDisabled={!canUnarchive}
                                            isLoading={
                                                unarchiveCropZone.isPending
                                            }
                                            onPress={() => onUnarchive(zone)}
                                        >
                                            <FontAwesomeIcon
                                                icon={faBoxOpen}
                                                size="sm"
                                            />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                );
            })}
            {!cropZones.length && (
                <div className="flex flex-row gap-2 items-center justify-center py-4">
                    <div className="text-sm text-gray-500">
                        {t("crop_zones.no_crop_zones", { ns: "setup" })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default CropZones;
